import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Link2, Loader2, Search } from 'lucide-react';

import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { getDomains } from '@/services/api/domains';
import {
    getGranulations, getRunSegments, getRuns, getSetupByDomain, resolveSlugs,
} from '@/services/api/price-structure';
import type { SegmentResult } from '@/types/price-structure';

import { OUTCOME_LABEL, OUTCOME_STYLE, asArray } from './constants';
import { GranulationTree, type TreeBlock } from './components/GranulationTree';
import { RulesView } from './components/RulesView';

type Tab = 'rules' | 'granulations' | 'runs' | 'audit';

export default function Results() {
    const { domainId: domainIdParam } = useParams<{ domainId: string }>();
    const domainId = Number(domainIdParam);
    const navigate = useNavigate();

    const [tab, setTab] = useState<Tab>('rules');
    const [search, setSearch] = useState('');
    const [selectedRun, setSelectedRun] = useState<number | null>(null);
    const [slugs, setSlugs] = useState<Record<string, string | null>>({});
    const { toast } = useToast();

    const { data: domains } = useQuery({ queryKey: ['domains'], queryFn: getDomains });
    const domain = asArray<any>(domains).find((d) => d.id === domainId);

    const { data: setup } = useQuery({
        queryKey: ['price-structure-setup-by-domain', domainId],
        queryFn: () => getSetupByDomain(domainId),
        retry: false,
    });

    const { data: granulations, isLoading: granulationsLoading } = useQuery({
        queryKey: ['price-structure-granulations', domainId],
        queryFn: () => getGranulations(domainId),
        enabled: tab === 'granulations',
    });

    const { data: runs, isLoading: runsLoading } = useQuery({
        queryKey: ['price-structure-runs', setup?.id],
        queryFn: () => getRuns(setup!.id),
        enabled: !!setup?.id && (tab === 'runs' || tab === 'audit'),
    });

    const activeRun = selectedRun ?? runs?.[0]?.id ?? null;

    const { data: segments, isLoading: segmentsLoading } = useQuery({
        queryKey: ['price-structure-run-segments', activeRun],
        queryFn: () => getRunSegments(activeRun!),
        enabled: tab === 'audit' && !!activeRun,
    });

    /** A API devolve post → país → bloco; a árvore precisa da lista achatada. */
    const allItems = useMemo<TreeBlock[]>(() => {
        const items: TreeBlock[] = [];
        Object.entries(granulations?.granulations ?? {}).forEach(([post, byCountry]) =>
            Object.entries(byCountry).forEach(([country, byBlock]) =>
                Object.entries(byBlock).forEach(([block, steps]) =>
                    items.push({ country, post, block, steps }),
                ),
            ),
        );
        return items;
    }, [granulations]);

    const needle = search.trim().toLowerCase();

    const items = useMemo(
        () =>
            needle
                ? allItems.filter(
                      (i) =>
                          i.post.toLowerCase().includes(needle) ||
                          i.country.toLowerCase().includes(needle) ||
                          i.block.toLowerCase().includes(needle) ||
                          (slugs[i.post] ?? '').toLowerCase().includes(needle),
                  )
                : allItems,
        [allItems, needle, slugs],
    );

    const postIds = useMemo(() => [...new Set(allItems.map((i) => i.post))], [allItems]);

    const slugMutation = useMutation({
        mutationFn: () => resolveSlugs(domainId, postIds),
        onSuccess: (data) => {
            setSlugs(data.slugs);
            const found = Object.values(data.slugs).filter(Boolean).length;
            toast({
                title: 'URLs tratadas',
                description: `${found} de ${postIds.length} post(s) com slug encontrado no WordPress.`,
                variant: found === 0 ? 'destructive' : undefined,
            });
        },
        onError: (error: any) =>
            toast({
                variant: 'destructive',
                title: 'Erro ao tratar URLs',
                description: error.response?.data?.detail || 'Falha ao consultar o WordPress.',
            }),
    });

    return (
        <div className="p-6 space-y-6">
            <div>
                <button
                    onClick={() => navigate('/price-structure')}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-2"
                >
                    <ArrowLeft className="h-4 w-4" /> Voltar
                </button>
                <h1 className="text-2xl font-semibold text-gray-900">
                    {domain?.domain ?? `Domínio #${domainId}`}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                    Granulações geradas pelo motor, histórico de execuções e o rastro de cada decisão.
                </p>
            </div>

            <div className="flex gap-1 border-b border-gray-200">
                <TabButton active={tab === 'rules'} onClick={() => setTab('rules')}>
                    Regras
                </TabButton>
                <TabButton active={tab === 'granulations'} onClick={() => setTab('granulations')}>
                    Granulações
                    {granulations ? ` (${granulations.total_segments})` : ''}
                </TabButton>
                <TabButton active={tab === 'runs'} onClick={() => setTab('runs')}>
                    Execuções
                </TabButton>
                <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>
                    Auditoria
                </TabButton>
            </div>

            {tab === 'rules' &&
                (setup ? (
                    <RulesView setup={setup} domainId={domainId} domainName={domain?.domain} />
                ) : (
                    <Empty message="Este domínio não tem setup de estrutura." />
                ))}

            {tab === 'granulations' && (
                <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative min-w-[240px] flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Filtrar por país, post, slug ou bloco..."
                                className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!postIds.length || slugMutation.isPending}
                            onClick={() => slugMutation.mutate()}
                        >
                            {slugMutation.isPending ? (
                                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Link2 className="mr-2 h-3.5 w-3.5" />
                            )}
                            Tratar URLs
                        </Button>
                    </div>

                    {granulationsLoading && <Loading />}

                    {!granulationsLoading && (
                        <GranulationTree
                            items={items}
                            forceExpanded={needle !== ''}
                            domainName={domain?.domain}
                            slugs={slugs}
                            emptyMessage={
                                needle
                                    ? 'Nenhum bloco corresponde ao filtro.'
                                    : 'Nenhuma granulação gerada ainda. Rode o setup para produzir a primeira.'
                            }
                        />
                    )}
                </div>
            )}

            {tab === 'runs' && (
                <div className="space-y-3">
                    {runsLoading && <Loading />}
                    {!runsLoading && asArray(runs).length === 0 && (
                        <Empty message="Nenhuma execução registrada." />
                    )}
                    {asArray<any>(runs).map((run) => (
                        <div
                            key={run.id}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3"
                        >
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">#{run.id}</span>
                                    <span
                                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                            run.status === 'completed'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : run.status === 'failed'
                                                  ? 'bg-red-100 text-red-700'
                                                  : 'bg-blue-100 text-blue-700'
                                        }`}
                                    >
                                        {run.status}
                                    </span>
                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                        {run.trigger}
                                    </span>
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                    janela {run.window_start} → {run.window_end} · iniciado{' '}
                                    {new Date(run.started_at).toLocaleString('pt-BR')}
                                </div>
                                {run.error_message && (
                                    <div className="mt-1 text-xs text-red-600">{run.error_message}</div>
                                )}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-600">
                                <span>{run.segments_found} encontrados</span>
                                <span className="text-emerald-700">{run.segments_written} gravados</span>
                                <span className="text-amber-700">{run.segments_skipped} pulados</span>
                                <button
                                    onClick={() => {
                                        setSelectedRun(run.id);
                                        setTab('audit');
                                    }}
                                    className="rounded-lg border border-gray-200 px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Ver decisões
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === 'audit' && (
                <div className="space-y-3">
                    {!activeRun && <Empty message="Nenhuma execução para auditar." />}
                    {segmentsLoading && <Loading />}
                    {activeRun && !segmentsLoading && (
                        <>
                            <p className="text-sm text-gray-500">
                                Execução <span className="font-medium text-gray-800">#{activeRun}</span> ·{' '}
                                {asArray(segments).length} segmentos
                            </p>
                            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                                        <tr>
                                            <th className="px-3 py-2.5 font-medium">Bloco</th>
                                            <th className="px-3 py-2.5 font-medium">País</th>
                                            <th className="px-3 py-2.5 font-medium">Post</th>
                                            <th className="px-3 py-2.5 font-medium text-right">Impr.</th>
                                            <th className="px-3 py-2.5 font-medium text-right">eCPM</th>
                                            <th className="px-3 py-2.5 font-medium text-right">PMR</th>
                                            <th className="px-3 py-2.5 font-medium text-right">Δ alvo</th>
                                            <th className="px-3 py-2.5 font-medium text-right">Shrink</th>
                                            <th className="px-3 py-2.5 font-medium text-right">Topo → Piso</th>
                                            <th className="px-3 py-2.5 font-medium text-right">Steps</th>
                                            <th className="px-3 py-2.5 font-medium">Resultado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {asArray<SegmentResult>(segments).map((segment) => (
                                            <AuditRow key={segment.id} segment={segment} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function AuditRow({ segment }: { segment: SegmentResult }) {
    const number = (value: number | null, digits = 2) =>
        value === null || value === undefined ? '—' : Number(value).toFixed(digits);

    return (
        <tr className="hover:bg-gray-50/50">
            <td className="px-3 py-2.5 text-gray-700">{segment.ad_unit}</td>
            <td className="px-3 py-2.5 text-gray-700">{segment.country}</td>
            <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{segment.custom_value}</td>
            <td className="px-3 py-2.5 text-right text-gray-700">{segment.impressions ?? '—'}</td>
            <td className="px-3 py-2.5 text-right text-gray-700">{number(segment.ecpm)}</td>
            <td className="px-3 py-2.5 text-right text-gray-700">{number(segment.pmr)}%</td>
            <td
                className={`px-3 py-2.5 text-right ${
                    (segment.pmr_delta ?? 0) < 0 ? 'text-red-600' : 'text-emerald-700'
                }`}
            >
                {segment.pmr_delta === null ? '—' : `${segment.pmr_delta > 0 ? '+' : ''}${number(segment.pmr_delta)}`}
            </td>
            <td className="px-3 py-2.5 text-right text-gray-700">{number(segment.shrink, 3)}</td>
            <td className="px-3 py-2.5 text-right text-gray-700">
                {number(segment.anchor_top)} → {number(segment.anchor_bottom)}
            </td>
            <td className="px-3 py-2.5 text-right text-gray-700">
                {segment.steps_delivered ?? 0}/{segment.steps_requested ?? 0}
            </td>
            <td className="px-3 py-2.5">
                <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${OUTCOME_STYLE[segment.outcome]}`}
                    title={segment.degradation_reason ?? undefined}
                >
                    {OUTCOME_LABEL[segment.outcome]}
                </span>
            </td>
        </tr>
    );
}

function TabButton({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
        >
            {children}
        </button>
    );
}

function Loading() {
    return (
        <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-gray-600">Carregando...</span>
            </div>
        </div>
    );
}

function Empty({ message }: { message: string }) {
    return (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white py-12 text-center text-sm text-gray-500">
            {message}
        </div>
    );
}
